import { useMemo } from "react";

import { useAtomValue } from "jotai/utils";

import { useFetchDataFileStats } from "./file-stats";
import { useGetTypesForFile } from "./column-types";
import { useGetDroppedColumns } from "./keep-drop";
import { fileOutlierTreatmentSpec } from "./outlier-treatment";
import { fileMissingTreatmentSpec } from "./missing-treatment";
import { fileStringTransforms } from "./string-transforms";

export const useGetCleanupSpec = (fileId: string) => {
  const colTypes = useGetTypesForFile(fileId);
  const droppedColumns = useGetDroppedColumns(fileId);
  const missingTreatment = useAtomValue(fileMissingTreatmentSpec(fileId));
  const outlierTreatment = useAtomValue(fileOutlierTreatmentSpec(fileId));
  const stringTransforms = useAtomValue(fileStringTransforms(fileId));

  return useMemo(
    () => ({
      column_types: colTypes,
      columns_to_drop: droppedColumns,
      missing_treatment: missingTreatment,
      outlier_treatment: outlierTreatment,
      string_transforms: stringTransforms,
    }),
    [
      colTypes,
      droppedColumns,
      missingTreatment,
      outlierTreatment,
      stringTransforms,
    ]
  );
};

export const useGetInvalidCleanupCols = (fileId: string) => {
  const spec = useGetCleanupSpec(fileId);
  const { data: stats } = useFetchDataFileStats(fileId);
  const colTypes = useGetTypesForFile(fileId);

  const colsWithMissingValues = Object.keys(stats || {})
    .filter((c) => {
      const colStats = stats?.[c]?.[colTypes[c]];
      if (!colStats) {
        return true;
      }
      return colStats.count < colStats.size;
    })
    .filter((c) => !spec.columns_to_drop.includes(c));

  const invalidMissingTreatmentCols = colsWithMissingValues.filter((c) => {
    const t = spec.missing_treatment.find((mt) => mt.column === c);
    if (!t) {
      return true;
    }
    return t.treatment === "ignore";
  });

  return invalidMissingTreatmentCols;
};
